<template lang="pug">
v-dialog(v-model="dialog" max-width="900px")
    template(v-slot:activator="{ on, attrs }")
        v-btn(color="primary" dark v-bind="attrs" v-on="on" small)
            v-icon(left) mdi-account-multiple-plus
            | Manage Invitations
    
    v-card
        v-card-title
            span.headline Group Invitations: {{ groupName }}
            v-spacer
            v-btn(icon @click="dialog = false")
                v-icon mdi-close
        
        v-card-text
            v-container
                v-row
                    v-col(cols="12")
                        v-text-field(
                            v-model="searchEmail"
                            label="Search by email"
                            prepend-icon="mdi-magnify"
                            clearable
                        )
                
                v-row
                    v-col(cols="12")
                        h3 Invite New Member
                        v-autocomplete(
                            v-model="selectedUserId"
                            :items="usersList"
                            :loading="isLoadingUsers"
                            :search-input.sync="userSearch"
                            item-text="email"
                            item-value="id"
                            label="Select user to invite"
                            clearable
                            prepend-icon="mdi-account-search"
                            no-data-text="Type at least 3 characters to search users"
                            hint="Search by name or email"
                            persistent-hint
                        )
                            template(v-slot:item="{ item }")
                                v-list-item-avatar
                                    img(:src="item.profile_picture_thumb")
                                v-list-item-content
                                    v-list-item-title {{ item.fname }} {{ item.lname }}
                                    v-list-item-subtitle {{ item.email }}
                        
                        v-btn(color="primary" @click="inviteUser" :disabled="!selectedUserId || inviting")
                            v-icon(left) mdi-send
                            | Send Invitation

                v-divider.my-4

                v-row
                    v-col(cols="12")
                        h3 Pending Invitations
                        
                        v-data-table(
                            :headers="invitationHeaders"
                            :items="pendingInvitations"
                            :loading="loadingInvitations"
                            loading-text="Loading invitations..."
                        )
                            template(v-slot:item.user="{ item }")
                                span {{ getUserName(item.user_id) }}
                            
                            template(v-slot:item.email="{ item }")
                                span {{ getUserEmail(item.user_id) }}
                            
                            template(v-slot:item.invited_by="{ item }")
                                span {{ getUserName(item.invited_by) }}
                            
                            template(v-slot:item.created_at="{ item }")
                                span {{ item.created_at | dateFormat }}
                            
                            template(v-slot:item.expires_at="{ item }")
                                span {{ item.expires_at | dateFormat }}
                            
                            template(v-slot:item.actions="{ item }")
                                v-btn(icon @click="revokeInvitation(item.id)" color="red")
                                    v-icon mdi-close-circle

        v-card-actions
            v-spacer
            v-btn(color="primary" text @click="dialog = false") Close

        v-snackbar(v-model="snackbar.show" :color="snackbar.color" top)
            | {{ snackbar.message }}
</template>

<script>
import { mapActions } from 'vuex';
import moment from 'moment';

export default {
    props: {
        groupId: {
            type: Number,
            required: true
        },
        groupName: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            dialog: false,
            searchEmail: '',
            selectedUserId: null,
            userSearch: null,
            isLoadingUsers: false,
            inviting: false,
            loadingInvitations: false,
            usersList: [],
            usersCache: {},
            invitationHeaders: [
                { text: 'User', value: 'user' },
                { text: 'Email', value: 'email' },
                { text: 'Invited By', value: 'invited_by' },
                { text: 'Date Sent', value: 'created_at' },
                { text: 'Expires', value: 'expires_at' },
                { text: 'Actions', value: 'actions', sortable: false }
            ],
            snackbar: {
                show: false,
                message: '',
                color: 'success'
            }
        };
    },
    computed: {
        pendingInvitations() {
            const { BhGroupInvitations } = this.$FeathersVuex.api;
            return BhGroupInvitations.findInStore({
                query: {
                    group_id: this.groupId,
                    status: 'pending'
                }
            }).data;
        }
    },
    watch: {
        dialog(val) {
            if (val) {
                this.loadInvitations();
            }
        },
        userSearch(val) {
            if (val && val.length > 2) {
                this.searchUsers(val);
            }
        }
    },
    filters: {
        dateFormat(date) {
            return date ? moment(date).format('lll') : 'n/a';
        }
    },
    methods: {
        async loadInvitations() {
            this.loadingInvitations = true;
            try {
                const result = await this.findInvitations({
                    query: {
                        group_id: this.groupId,
                        status: 'pending'
                    }
                });

                // Load user data for all invitations
                const invitations = result.data || [];
                const userIds = new Set();
                invitations.forEach(inv => {
                    if (inv.user_id) userIds.add(inv.user_id);
                    if (inv.invited_by) userIds.add(inv.invited_by);
                });

                for (const userId of userIds) {
                    if (!this.usersCache[userId]) {
                        try {
                            const user = await this.$store.dispatch('users/get', userId);
                            this.$set(this.usersCache, userId, user);
                        } catch (e) {
                            this.$set(this.usersCache, userId, { fname: 'Unknown', lname: 'User', email: 'N/A' });
                        }
                    }
                }
            } catch (error) {
                this.showError('Failed to load invitations');
            } finally {
                this.loadingInvitations = false;
            }
        },
        async searchUsers(search) {
            this.isLoadingUsers = true;
            try {
                const result = await this.findUsers({
                    query: {
                        $or: [
                            { email: { $like: `%${search}%` } },
                            { fname: { $like: `%${search}%` } },
                            { lname: { $like: `%${search}%` } }
                        ],
                        $limit: 20
                    }
                });
                this.usersList = result.data || [];
            } catch (error) {
                this.showError('Failed to search users');
            } finally {
                this.isLoadingUsers = false;
            }
        },
        async inviteUser() {
            if (!this.selectedUserId) return;
            
            this.inviting = true;
            try {
                await this.createInvitation({
                    group_id: this.groupId,
                    user_id: this.selectedUserId
                });
                
                this.showSuccess('Invitation sent successfully');
                this.selectedUserId = null;
                this.userSearch = null;
                this.loadInvitations();
            } catch (error) {
                this.showError(error.message || 'Failed to send invitation');
            } finally {
                this.inviting = false;
            }
        },
        async revokeInvitation(invitationId) {
            try {
                await this.patchInvitation([
                    invitationId,
                    { status: 'revoked', revoked_at: new Date() }
                ]);
                
                this.showSuccess('Invitation revoked');
                this.loadInvitations();
            } catch (error) {
                this.showError('Failed to revoke invitation');
            }
        },
        showSuccess(message) {
            this.snackbar = {
                show: true,
                message,
                color: 'success'
            };
        },
        showError(message) {
            this.snackbar = {
                show: true,
                message,
                color: 'error'
            };
        },
        getUserName(userId) {
            const user = this.usersCache[userId];
            return user ? `${user.fname} ${user.lname}` : 'Loading...';
        },
        getUserEmail(userId) {
            const user = this.usersCache[userId];
            return user ? user.email : 'Loading...';
        },
        ...mapActions('bh-group-invitations', {
            findInvitations: 'find',
            createInvitation: 'create',
            patchInvitation: 'patch'
        }),
        ...mapActions('users', { findUsers: 'find' })
    }
};
</script>
